import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FaExternalLinkAlt, FaGithub, FaFilter, FaShoppingBag, FaBriefcase, FaCode, FaMobile } from 'react-icons/fa';
import BookAppointment from '../components/BookAppointment';

const PortfolioSection = styled.section`
  padding: 5rem 0;
  background: #0a0a0a;
`;

const PortfolioContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 3rem;
  
  h2 {
    font-size: clamp(2rem, 4vw, 3rem);
    margin-bottom: 1rem;
    color: #ffffff;
  }
  
  p {
    font-size: 1.2rem;
    color: #cccccc;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const FilterButtons = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 3rem;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1.5rem;
  background: ${props => props.active ? '#4682B4' : '#1a1a1a'};
  color: ${props => props.active ? '#000000' : '#ffffff'};
  border: 2px solid #4682B4;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  
  &:hover {
    background: #4682B4;
    color: #000000;
    transform: translateY(-2px);
  }
`;

const PortfolioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled(motion.div)`
  background: #1a1a1a;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid rgba(70, 130, 180, 0.1);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    border-color: rgba(70, 130, 180, 0.3);
  }
`;

const ProjectImage = styled.div`
  height: 250px;
  background: linear-gradient(135deg, #4682B4 0%, #87CEEB 100%);
  position: relative;
  overflow: hidden;
  
  .website-preview {
    width: 100%;
    height: 100%;
    border: none;
    transform: scale(0.5);
    transform-origin: 0 0;
    width: 200%;
    height: 200%;
    transition: transform 0.3s ease;
  }
  
  .website-screenshot {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  .image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000000;
    font-size: 1.1rem;
    text-align: center;
    padding: 2rem;
  }
  
  .project-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover .project-overlay {
    opacity: 1;
  }
  
  &:hover .website-screenshot {
    transform: scale(1.05);
  }
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const ProjectLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  background: #4682B4;
  border-radius: 50%;
  color: #000000;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: #87CEEB;
    transform: scale(1.1);
  }
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
`;

const ProjectTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: #ffffff;
`;

const ProjectDescription = styled.p`
  color: #cccccc;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const ProjectTech = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const TechTag = styled.span`
  background: #2a2a2a;
  color: #cccccc;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.875rem;
  border: 1px solid rgba(70, 130, 180, 0.2);
`;

const ProjectCategory = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, #4682B4 0%, #87CEEB 100%);
  color: #000000;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.875rem;
  font-weight: 500;
`;

const Portfolio = () => {
  const [filter, setFilter] = useState('all');
  const [imageErrors, setImageErrors] = useState({});

  const projects = [
    {
      id: 1,
      title: "Loom & Local",
      description: "A comprehensive multi-vendor e-commerce platform built with React and Laravel, featuring user authentication, product management, shopping cart, payment integration, seller dashboard, and admin panel with full CRUD operations.",
      image: "loomandlocal",
      category: "ecommerce",
      technologies: ["React", "Laravel", "MySQL", "PHP", "JavaScript", "CSS"],
      liveLink: "https://www.loomandlocal.com",
      githubLink: "https://github.com",
      screenshotUrl: `https://www.loomandlocal.com`
    },
    {
      id: 2,
      title: "ShoppetX",
      description: "A modern Shopify e-commerce store featuring trending products, popular collections, multi-language support (English, Spanish, Arabic), customer accounts, wishlist functionality, and comprehensive pet health advice section.",
      image: "shoppetx",
      category: "ecommerce",
      technologies: ["Shopify", "Liquid", "JavaScript", "CSS", "Responsive Design"],
      liveLink: "https://shoppetx.com",
      githubLink: "https://github.com",
      screenshotUrl: `https://shoppetx.com`
    },
    {
      id: 3,
      title: "Icon Creativity",
      description: "A professional construction company website built with WordPress, featuring 28 years of business showcase, service portfolio, project gallery, client testimonials, and comprehensive contact management system.",
      image: "iconcreativity",
      category: "business",
      technologies: ["WordPress", "PHP", "MySQL", "JavaScript", "CSS", "Responsive Design"],
      liveLink: "https://iconcreativity.com.pk",
      githubLink: "https://github.com",
      screenshotUrl: `https://iconcreativity.com.pk`
    },
    {
      id: 4,
      title: "E-commerce Fashion Store",
      description: "A modern e-commerce platform built with React and Shopify, featuring advanced filtering, shopping cart functionality, and seamless checkout process.",
      image: "fashion-store",
      category: "ecommerce",
      technologies: ["React", "Shopify", "Node.js", "MongoDB", "Stripe"],
      liveLink: "https://example.com",
      githubLink: "https://github.com",
      screenshotUrl: `https://example.com`
    },
    {
      id: 5,
      title: "Kaj Outfits",
      description: "A trendy fashion e-commerce platform built on Shopify, specializing in modern outfits and accessories, featuring product catalogs, shopping cart, secure checkout, and responsive design for optimal mobile shopping experience.",
      image: "kaj-outfits",
      category: "ecommerce",
      technologies: ["Shopify", "Liquid", "JavaScript", "CSS", "Responsive Design"],
      liveLink: "https://kajoutfits.com",
      githubLink: "https://github.com",
      screenshotUrl: `https://kajoutfits.com`
    },
   
  ];

  const categories = [
    { id: 'all', label: 'All Projects', icon: <FaFilter /> },
    { id: 'ecommerce', label: 'E-commerce', icon: <FaShoppingBag /> },
    { id: 'business', label: 'Business', icon: <FaBriefcase /> },
    { id: 'mobile', label: 'Mobile', icon: <FaMobile /> },
    { id: 'custom', label: 'Custom', icon: <FaCode /> }
  ];

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => project.category === filter);

  const handleImageError = (projectId) => {
    setImageErrors(prev => ({
      ...prev,
      [projectId]: true
    }));
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <PortfolioSection>
      <PortfolioContainer>
        <SectionHeader
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2>Our Portfolio</h2>
          <p>
            Explore our latest projects showcasing our expertise in e-commerce, business websites, 
            and custom web solutions built with modern technologies.
          </p>
        </SectionHeader>

        <FilterButtons>
          {categories.map((category) => (
            <FilterButton
              key={category.id}
              active={filter === category.id}
              onClick={() => setFilter(category.id)}
            >
              {category.icon} {category.label}
            </FilterButton>
          ))}
        </FilterButtons>

        <PortfolioGrid
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredProjects.map((project, index) => (
            <ProjectCard
              key={project.id}
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
            >
              <ProjectImage>
                {!imageErrors[project.id] && project.screenshotUrl ? (
                  <iframe 
                    src={project.screenshotUrl} 
                    className="website-preview"
                    title={`${project.title} preview`}
                    onError={() => handleImageError(project.id)}
                    loading="lazy"
                    sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                  />
                ) : (
                  <div className="image-placeholder">
                    <div>
                      <h3>{project.title}</h3>
                      <p>Project Preview</p>
                    </div>
                  </div>
                )}
                <div className="project-overlay">
                  <ProjectLinks>
                    <ProjectLink 
                      href={project.liveLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <FaExternalLinkAlt />
                    </ProjectLink>
                    <ProjectLink 
                      href={project.githubLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <FaGithub />
                    </ProjectLink>
                  </ProjectLinks>
                </div>
              </ProjectImage>
              <ProjectContent>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>
                <ProjectTech>
                  {project.technologies.map((tech, techIndex) => (
                    <TechTag key={techIndex}>{tech}</TechTag>
                  ))}
                </ProjectTech>
                <ProjectCategory>
                  {categories.find(cat => cat.id === project.category)?.label}
                </ProjectCategory>
              </ProjectContent>
            </ProjectCard>
          ))}
        </PortfolioGrid>
      </PortfolioContainer>
      
      <div style={{ marginTop: '4rem' }}>
        <BookAppointment />
      </div>
    </PortfolioSection>
  );
};

export default Portfolio;
